import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Box, Column, Icon } from '~/commonComponents';
import { Button } from '@tempmee/react';
import { FormActions } from '~/components/shared/forms';
import { SectionCard, ShiftRequestHeader } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { fireShiftRequestFillPreferencesSubmitted } from '~/posthogs/events';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { SpeedUpCoverageSection } from './SpeedUpCoverageSection';

export const AddInvitesScreen = () => {
  const { control, watch } = useFormContext<IShiftDetailsFormValues>();
  const navigate = useNavigate();

  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/info');
  }, [allowNavigation, navigate]);

  const handleReviewShifts = useCallback(() => {
    fireShiftRequestFillPreferencesSubmitted();
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [allowNavigation, navigate]);

  return (
    <ShiftRequestHeader currentStep={2} onBack={handleBack} maxWidth={1120}>
      <Column gap={24} maxWidth={730} alignSelf="center" width="100%">
        <SectionCard title="Speed Up Your Coverage" testId="speed-up-card">
          <SpeedUpCoverageSection control={control} watch={watch} />
        </SectionCard>
      </Column>

      <FormActions>
        <Button
          data-testid="review-shifts-button"
          data-cy="review-shifts-button"
          onClick={handleReviewShifts}
        >
          Review Shifts
        </Button>
      </FormActions>

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Box marginBottom={20}>
              <Icon name="warning-outline" size={80} />
            </Box>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}
    </ShiftRequestHeader>
  );
};
